import {CONCEPT_FIELD_LABEL} from '../format';
import {useStudio} from '../store';
import type {Concept, ConceptField} from '../types';

import {Editable} from './Editable';
import {Skeleton} from './Elapsed';
import {Pencil, Regenerate} from './Icons';

const FIELDS = Object.keys(CONCEPT_FIELD_LABEL) as ConceptField[];

/**
 * The creative direction, four fields, each independently editable and regenerable.
 *
 * Per-field regeneration asks the backend for that field alone rather than re-rolling
 * the whole concept, so regenerating the hook cannot quietly discard a tone the user
 * just wrote by hand.
 */
export function ConceptPanel({concept}: {concept: Concept | null}) {
  const s = useStudio();
  const allBusy = s.isBusy('concept:all');

  return (
    <div className="panel">
      <div className="panel-head">
        <div style={{display: 'flex', alignItems: 'center', gap: 12, minWidth: 0}}>
          <span className="chip solid">الفكرة الإبداعية</span>
          <span
            className="h-note"
            style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
          >
            {s.project?.title}
          </span>
        </div>
        <button
          type="button"
          className="btn-out"
          disabled={allBusy || !concept}
          onClick={() => void s.regenerateConcept()}
        >
          {allBusy ? <span className="spin" style={{width: 12, height: 12}} /> : <Regenerate size={12} />}
          أعد توليد الفكرة كاملة
        </button>
      </div>

      {!concept ? (
        <div style={{padding: '20px 22px'}} className="empty-note">
          لا توجد فكرة إبداعية بعد.
        </div>
      ) : (
        <div className="cf-grid">
          {FIELDS.map((field) => {
            const busy = allBusy || s.isBusy(`concept:${field}`);
            const error = s.errorFor(`concept:${field}`);
            return (
              <div className="cf" key={field}>
                <div className="cf-head">
                  <span className="field-lbl">{CONCEPT_FIELD_LABEL[field]}</span>
                  <div style={{display: 'flex', gap: 4}}>
                    <button
                      type="button"
                      className="btn-txt"
                      disabled={busy}
                      onClick={() => document.getElementById(`cf-${field}`)?.focus()}
                    >
                      <Pencil />
                      تحرير
                    </button>
                    <button
                      type="button"
                      className="btn-txt"
                      disabled={busy}
                      onClick={() => void s.regenerateConcept(field)}
                    >
                      <Regenerate />
                      أعد التوليد
                    </button>
                  </div>
                </div>

                {busy ? (
                  <div style={{display: 'flex', flexDirection: 'column', gap: 8, padding: '3px 0'}}>
                    <Skeleton />
                    <Skeleton width="78%" />
                    <div style={{font: '500 11.5px/1.6 var(--sans)', color: 'var(--state-work)'}}>
                      جاري إعادة التوليد…
                    </div>
                  </div>
                ) : (
                  <Editable
                    id={`cf-${field}`}
                    className="cf-val edit"
                    value={concept[field]}
                    onCommit={(next) => void s.editConcept(field, next)}
                  />
                )}
                {error && (
                  <div className="err-note" style={{marginTop: 6}}>
                    {error}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
